import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Navigation from '../components/Navigation';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { 
  Settings, 
  Menu, 
  Eye, 
  EyeOff, 
  Save, 
  RefreshCw, 
  Loader2,
  LayoutDashboard,
  BarChart3,
  Shield,
  ShieldCheck,
  Camera,
  Watch,
  FlaskConical,
  LineChart,
  Leaf,
  FileText,
  Calendar,
  Pill,
  Brain,
  Activity,
  Scan,
  MessageCircle,
  TrendingUp,
  Utensils,
  GripVertical,
  AlertCircle,
  CheckCircle,
  Bot,
  Users,
  PersonStanding,
  HeartPulse,
  Syringe,
  Umbrella,
  Lock,
  Baby,
  AlertTriangle,
  ScanLine,
  MapPin,
  HeartHandshake,
  Dna,
  Trophy
} from 'lucide-react';
import axios from 'axios';
import { API } from '../App';
import { toast } from 'sonner';

// Icon mapping - must match icons defined in server.py DEFAULT_MENU_ITEMS
const ICON_MAP = {
  LayoutDashboard,
  BarChart3,
  Shield,
  ShieldCheck,
  Camera,
  Watch,
  FlaskConical,
  LineChart,
  Leaf,
  FileText,
  Calendar,
  Pill,
  Brain,
  Activity,
  Scan,
  MessageCircle,
  TrendingUp,
  Utensils,
  Bot,
  Users,
  PersonStanding,
  HeartPulse,
  Syringe,
  Umbrella,
  Lock,
  Baby,
  AlertTriangle,
  ScanLine,
  MapPin,
  HeartHandshake,
  Dna,
  Trophy
};

const MenuSettings = ({ user, setUser }) => {
  const { t } = useTranslation();
  const [menuItems, setMenuItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    loadMenuSettings();
  }, []);

  const loadMenuSettings = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/menu-settings`);
      setMenuItems(response.data.menu_items);
      setHasChanges(false);
    } catch (error) {
      toast.error('Failed to load menu settings');
    } finally {
      setLoading(false);
    }
  };

  const toggleVisibility = (itemId) => {
    setMenuItems(prev => prev.map(item => 
      item.id === itemId ? { ...item, visible: !item.visible } : item
    ));
    setHasChanges(true);
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/menu-settings`, menuItems);
      toast.success('Menu settings saved successfully!');
      setHasChanges(false);
    } catch (error) {
      toast.error('Failed to save menu settings');
    } finally {
      setSaving(false);
    }
  };

  const resetToDefault = async () => {
    if (!confirm('Reset all menu items to default visibility?')) return;
    
    setMenuItems(prev => prev.map(item => ({ ...item, visible: true })));
    setHasChanges(true);
    toast.info('Menu items reset to default. Click Save to apply.');
  };

  const hideAll = () => {
    setMenuItems(prev => prev.map(item => 
      item.id === 'dashboard' ? item : { ...item, visible: false }
    ));
    setHasChanges(true);
  };

  const showAll = () => {
    setMenuItems(prev => prev.map(item => ({ ...item, visible: true })));
    setHasChanges(true);
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'patient': return 'bg-blue-100 text-blue-800';
      case 'doctor': return 'bg-green-100 text-green-800';
      case 'super_admin': return 'bg-purple-100 text-purple-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getIcon = (iconName) => {
    const IconComponent = ICON_MAP[iconName];
    return IconComponent ? <IconComponent className="w-5 h-5" /> : <Menu className="w-5 h-5" />;
  };

  const visibleCount = menuItems.filter(item => item.visible).length;
  const hiddenCount = menuItems.length - visibleCount;

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-indigo-50">
        <Navigation user={user} setUser={setUser} />
        <div className="container mx-auto px-4 py-6 pt-24 flex items-center justify-center">
          <Loader2 className="w-12 h-12 animate-spin text-purple-600" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-indigo-50">
      <Navigation user={user} setUser={setUser} />
      
      <div className="container mx-auto px-4 py-6 pt-24">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-800 flex items-center gap-3">
              <Settings className="w-8 h-8 text-purple-600" />
              Menu Settings
            </h1>
            <p className="text-gray-600 mt-1">Control which menu items are visible to users</p>
          </div>
          
          <div className="flex gap-3">
            <Button 
              variant="outline" 
              onClick={resetToDefault}
              disabled={saving}
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Reset Default
            </Button>
            <Button 
              onClick={saveSettings}
              disabled={!hasChanges || saving}
              className="bg-purple-600 hover:bg-purple-700"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Save Changes
                </>
              )}
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <Card className="bg-white">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                <Menu className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{menuItems.length}</p>
                <p className="text-sm text-gray-500">Total Items</p>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-white">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                <Eye className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-green-600">{visibleCount}</p>
                <p className="text-sm text-gray-500">Visible</p>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-white">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
                <EyeOff className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-red-600">{hiddenCount}</p>
                <p className="text-sm text-gray-500">Hidden</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Quick Actions */}
        <div className="flex gap-3 mb-6">
          <Button variant="outline" size="sm" onClick={showAll}>
            <Eye className="w-4 h-4 mr-2" />
            Show All
          </Button>
          <Button variant="outline" size="sm" onClick={hideAll}>
            <EyeOff className="w-4 h-4 mr-2" />
            Hide All (except Dashboard)
          </Button>
        </div>

        {/* Unsaved Changes Warning */}
        {hasChanges && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6 flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-yellow-600" />
            <span className="text-yellow-800">You have unsaved changes. Click "Save Changes" to apply.</span>
          </div>
        )}

        {/* Menu Items List */}
        <Card>
          <CardHeader>
            <CardTitle>Navigation Menu Items</CardTitle>
            <CardDescription>Toggle visibility for each menu item. Changes apply to all users.</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {menuItems.map((item, index) => (
                <div 
                  key={item.id}
                  className={`flex items-center justify-between p-4 rounded-lg border transition-all ${
                    item.visible 
                      ? 'bg-white border-gray-200 hover:border-purple-300' 
                      : 'bg-gray-50 border-gray-100 opacity-60'
                  }`}
                >
                  <div className="flex items-center gap-4">
                    <div className="text-gray-400">
                      <GripVertical className="w-5 h-5" />
                    </div>
                    
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                      item.visible ? 'bg-purple-100 text-purple-600' : 'bg-gray-200 text-gray-400'
                    }`}>
                      {getIcon(item.icon)}
                    </div>
                    
                    <div>
                      <div className="flex items-center gap-2">
                        <span className={`font-medium ${item.visible ? 'text-gray-800' : 'text-gray-500'}`}>
                          {item.label}
                        </span>
                        {item.id === 'dashboard' && (
                          <Badge variant="outline" className="text-xs">Required</Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-xs text-gray-400">{item.path}</span>
                        <span className="text-gray-300">•</span>
                        <div className="flex gap-1">
                          {item.roles?.map(role => (
                            <Badge key={role} className={`text-xs ${getRoleBadgeColor(role)}`}>
                              {role}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                      {item.visible ? (
                        <span className="text-sm text-green-600 flex items-center gap-1">
                          <CheckCircle className="w-4 h-4" />
                          Visible
                        </span>
                      ) : (
                        <span className="text-sm text-gray-400 flex items-center gap-1">
                          <EyeOff className="w-4 h-4" />
                          Hidden
                        </span>
                      )}
                    </div>
                    
                    <Switch
                      checked={item.visible}
                      onCheckedChange={() => toggleVisibility(item.id)}
                      disabled={item.id === 'dashboard'} // Dashboard always visible
                    />
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Info Note */}
        <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h4 className="font-medium text-blue-800 mb-2">ℹ️ How it works</h4>
          <ul className="text-sm text-blue-700 space-y-1">
            <li>• Hidden menu items will not appear in the navigation bar for any user</li>
            <li>• Dashboard cannot be hidden as it's the main entry point</li>
            <li>• Users with specific roles will only see items assigned to their role</li>
            <li>• Changes take effect immediately after saving</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default MenuSettings;
